import { Component, effect, inject, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { catchError, EMPTY, take, tap } from 'rxjs';
import { Courier } from 'src/app/shared/models/courier';
import { Shipment } from 'src/app/shared/models/shipment';
import { ShipmentBooking } from 'src/app/shared/models/shipment-booking';
import { AuthService } from 'src/app/shared/services/auth.service';
import { CouriersApiService } from 'src/app/shared/services/couriers-api.service';
import { ShipmentsApiService } from 'src/app/shared/services/shipments-api.service';

@Component({
  selector: 'app-book-shipment-stepper',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatStepperModule,
    MatSelectModule,
    MatButtonToggleModule,
    MatButton,
  ],
  templateUrl: './book-shipment-stepper.html',
  styleUrl: './book-shipment-stepper.scss',
})
export class BookShipmentStepper {
  navigateToShipment = output<Shipment>();
  bookShipment = output<ShipmentBooking>();
  cancel = output<void>();

  readonly formBuilder = inject(FormBuilder);
  readonly shipmentsApiService = inject(ShipmentsApiService);
  readonly couriersApiService = inject(CouriersApiService);
  readonly authService = inject(AuthService);

  couriers = signal<Courier[]>([]);
  stepper = viewChild<MatStepper>('stepper');

  formGroup = this.formBuilder.group({
    courier: [null as (string | null), Validators.required],
    weight: [null as (number | null), Validators.required]
  });

  get selectedCourierControl() {
    return this.formGroup.get('courier')! as FormControl
  }

  get selectedWeightControl() {
    return this.formGroup.get('weight')! as FormControl;
  }

  constructor() {
    this.couriersApiService.getCouriers().pipe(
      take(1),
      tap(couriers => {
        this.couriers.set(couriers);
      })
    ).subscribe();

    effect(() => {
      if (this.authService.isAuthenticated()) {
        this.couriers.update(couriers => couriers.map(courier => ({
          ...courier,
          enabled: courier.name.toLowerCase() === 'postnord' || this.authService.isInRole(courier.name)
        })));
      }
    });
    
    this.selectedCourierControl.valueChanges.pipe(
      takeUntilDestroyed(),
      tap((courier) => {
        this.selectedWeightControl?.reset();

        this.shipmentsApiService.getAssignedShipment(courier.name).pipe(
          take(1),
          tap(shipment => {
            this.navigateToShipment.emit(shipment);
          }),
          catchError(() => {
            const courier = this.selectedCourierControl.value;
            if (!courier?.requiresWeight) {
              this.selectedWeightControl.setValue(courier.weights[0]);
            }
            this.stepper()?.next();
            return EMPTY;
          })
        ).subscribe();

      })
    ).subscribe();

    this.selectedWeightControl.valueChanges.pipe(
      takeUntilDestroyed(),
      tap(weight => {
        if (weight != null) {
          this.stepper()?.next();
        }
      })
    ).subscribe();
  }

  onBookShipment(): void {
    if (this.formGroup.valid) {
      this.bookShipment.emit({
        courier: this.selectedCourierControl.value.name,
        weight: this.selectedWeightControl.value.toString()
      });
    }
  }

  onCancel(): void {
    this.cancel.emit();
  }
}
